import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
  Divider,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Cloud as WordCloudIcon,
  Psychology as AIIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { Wordcloud } from '@visx/wordcloud';
import { scaleLog } from '@visx/scale';
import { Text } from '@visx/text';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  Treemap,
} from 'recharts';

const colors = {
  primary: '#0078D4',
  success: '#107C10',
  warning: '#FFB900',
  danger: '#D13438',
  purple: '#5C2D91',
  teal: '#40E0D0',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438'],
};

// Word cloud data - comprehensive dataset for dense visualization
const wordCloudData = [
  { text: 'funnel', value: 120 },
  { text: 'conversion', value: 98 },
  { text: 'analytics', value: 85 },
  { text: 'metrics', value: 78 },
  { text: 'customer', value: 72 },
  { text: 'engagement', value: 68 },
  { text: 'journey', value: 65 },
  { text: 'optimization', value: 58 },
  { text: 'retention', value: 54 },
  { text: 'data', value: 52 },
  { text: 'pipeline', value: 48 },
  { text: 'dashboard', value: 45 },
  { text: 'insights', value: 42 },
  { text: 'behavior', value: 38 },
  { text: 'segmentation', value: 36 },
  { text: 'automation', value: 34 },
  { text: 'performance', value: 32 },
  { text: 'strategy', value: 30 },
  { text: 'revenue', value: 28 },
  { text: 'acquisition', value: 26 },
  { text: 'roi', value: 24 },
  { text: 'kpi', value: 22 },
  { text: 'benchmark', value: 20 },
  { text: 'attribution', value: 18 },
  { text: 'cohort', value: 16 },
  { text: 'targeting', value: 15 },
  { text: 'leads', value: 14 },
  { text: 'nurturing', value: 13 },
  { text: 'scoring', value: 12 },
  { text: 'qualification', value: 11 },
  { text: 'touchpoints', value: 10 },
  { text: 'lifecycle', value: 10 },
  { text: 'onboarding', value: 9 },
  { text: 'churn', value: 9 },
  { text: 'growth', value: 8 },
  { text: 'scale', value: 8 },
  { text: 'velocity', value: 7 },
  { text: 'feedback', value: 7 },
  { text: 'satisfaction', value: 6 },
  { text: 'loyalty', value: 6 },
  { text: 'advocacy', value: 5 },
  { text: 'value', value: 5 },
  { text: 'proposition', value: 4 },
  { text: 'messaging', value: 4 },
  { text: 'positioning', value: 4 },
  { text: 'channels', value: 4 },
  { text: 'touchpoint', value: 3 },
  { text: 'experience', value: 3 },
  { text: 'interaction', value: 3 },
  { text: 'response', value: 3 },
];

const topicTimeline = [
  { minute: 0, topic: 'Introduction & Overview', duration: 5 },
  { minute: 5, topic: 'What is a Conversion Funnel?', duration: 12 },
  { minute: 17, topic: 'Key Metrics & KPIs', duration: 15 },
  { minute: 32, topic: 'Analytics Tools Demo', duration: 18 },
  { minute: 50, topic: 'Real Case Studies', duration: 20 },
  { minute: 70, topic: 'Best Practices', duration: 12 },
  { minute: 82, topic: 'Q&A Session', duration: 10 },
];

const sentimentOverTime = Array.from({ length: 20 }, (_, i) => {
  const basePositive = 60 + Math.sin(i / 3) * 15;
  const baseNeutral = 30 - Math.sin(i / 3) * 10;
  return {
    segment: `${i * 5}m`,
    positive: Math.floor(basePositive + Math.random() * 5),
    neutral: Math.floor(baseNeutral + Math.random() * 5),
    negative: Math.floor(10 + Math.random() * 5),
  };
});

const speakerDistribution = [
  { speaker: 'Instructor', percentage: 68, color: colors.primary },
  { speaker: 'Student Questions', percentage: 22, color: colors.success },
  { speaker: 'Group Discussion', percentage: 10, color: colors.warning },
];

const questionCategories = [
  { category: 'Clarification', count: 18 },
  { category: 'Deep Dive', count: 12 },
  { category: 'Application', count: 15 },
  { category: 'Tools/Tech', count: 9 },
  { category: 'Examples', count: 11 },
];

const keyMoments = [
  { time: '10:17', moment: 'Peak engagement', description: 'Funnel visualization demo', engagement: 95 },
  { time: '10:34', moment: 'Question spike', description: 'Metrics calculation confusion', engagement: 72 },
  { time: '10:52', moment: 'Lightbulb moment', description: 'Real case study resonated', engagement: 92 },
  { time: '11:08', moment: 'Dip detected', description: 'Technical difficulties', engagement: 58 },
  { time: '11:22', moment: 'Strong finish', description: 'Q&A engagement high', engagement: 88 },
];

const topicsExtracted = [
  { topic: 'Conversion Optimization', mentions: 42, sentiment: 0.85 },
  { topic: 'Customer Journey', mentions: 38, sentiment: 0.78 },
  { topic: 'Analytics Tools', mentions: 35, sentiment: 0.72 },
  { topic: 'Data-Driven Decisions', mentions: 28, sentiment: 0.80 },
  { topic: 'Retention Strategies', mentions: 22, sentiment: 0.76 },
];

const TranscriptAnalysis = () => {
  const [selectedView, setSelectedView] = useState('overview');

  // Word cloud configuration (following dev.to tutorial)
  const MIN_FONT_SIZE = 14;
  const MAX_FONT_SIZE = 48;
  const MIN_FONT_WEIGHT = 400;
  const MAX_FONT_WEIGHT = 700;

  const sortedWords = React.useMemo(
    () => {
      const sorted = [...wordCloudData].sort((a, b) => b.value - a.value);
      console.log('Word cloud data ready:', sorted.length, 'words');
      return sorted;
    },
    []
  );

  React.useEffect(() => {
    console.log('TranscriptAnalysis mounted, selectedView:', selectedView);
    console.log('Word cloud will render with:', sortedWords.length, 'words');
  }, [selectedView, sortedWords]);

  const [minOccurrences, maxOccurrences] = React.useMemo(() => {
    const min = Math.min(...sortedWords.map((w) => w.value));
    const max = Math.max(...sortedWords.map((w) => w.value));
    return [min, max];
  }, [sortedWords]);

  const calculateFontSize = React.useCallback(
    (wordOccurrences) => {
      const normalizedValue = (wordOccurrences - minOccurrences) / (maxOccurrences - minOccurrences);
      const fontSize = MIN_FONT_SIZE + normalizedValue * (MAX_FONT_SIZE - MIN_FONT_SIZE);
      return Math.round(fontSize);
    },
    [maxOccurrences, minOccurrences]
  );

  const calculateFontWeight = React.useCallback(
    (wordOccurrences) => {
      const normalizedValue = (wordOccurrences - minOccurrences) / (maxOccurrences - minOccurrences);
      const fontWeight = MIN_FONT_WEIGHT + normalizedValue * (MAX_FONT_WEIGHT - MIN_FONT_WEIGHT);
      return Math.round(fontWeight);
    },
    [maxOccurrences, minOccurrences]
  );

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          🤖 AI-Powered Transcript Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <Chip label="Marketing Analytics 101" color="primary" size="small" />
          <Chip label="Session 12 • Oct 11, 2025" variant="outlined" size="small" />
          <Chip label="92 min transcript" variant="outlined" size="small" />
          <Chip label="✓ AI Processed" sx={{ bgcolor: colors.purple, color: 'white' }} size="small" />
        </Box>
        <ButtonGroup size="small">
          <Button variant={selectedView === 'overview' ? 'contained' : 'outlined'} onClick={() => setSelectedView('overview')}>
            Overview
          </Button>
          <Button variant={selectedView === 'topics' ? 'contained' : 'outlined'} onClick={() => setSelectedView('topics')}>
            Topics
          </Button>
          <Button variant={selectedView === 'sentiment' ? 'contained' : 'outlined'} onClick={() => setSelectedView('sentiment')}>
            Sentiment
          </Button>
          <Button variant={selectedView === 'questions' ? 'contained' : 'outlined'} onClick={() => setSelectedView('questions')}>
            Questions
          </Button>
        </ButtonGroup>
      </Box>

      {/* Content based on selected view */}
      {selectedView === 'overview' && (
        <>
          {/* Word Cloud Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={7}>
              <Paper sx={{ p: 3, height: 500 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📊 Word Cloud - Most Mentioned Terms
                </Typography>
                <div style={{ 
                  width: '100%', 
                  height: 420, 
                  backgroundColor: '#fafafa', 
                  borderRadius: 8, 
                  border: '1px solid #e0e0e0', 
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Wordcloud
                    words={sortedWords}
                    width={650}
                    height={400}
                    fontSize={(datum) => calculateFontSize(datum.value)}
                    font="'Arial', sans-serif"
                    padding={2}
                    spiral="rectangular"
                    rotate={0}
                    random={Math.random}
                  >
                    {(cloudWords) => (
                      <g>
                        {cloudWords.map((w, i) => (
                          <text
                            key={w.text}
                            fill={colors.chart[i % colors.chart.length]}
                            textAnchor="middle"
                            transform={`translate(${w.x}, ${w.y}) rotate(${w.rotate})`}
                            fontSize={w.size}
                            fontFamily={w.font}
                            fontWeight={calculateFontWeight(w.value || 0)}
                            style={{ cursor: 'pointer' }}
                            onClick={() => alert(`${w.text}: ${w.value} mentions`)}
                          >
                            {w.text}
                          </text>
                        ))}
                      </g>
                    )}
                  </Wordcloud>
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      color: '#999',
                      fontSize: '0.7rem',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {sortedWords.length} terms
                  </Typography>
                </div>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper sx={{ p: 3, height: 500 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Speaker Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={420}>
                  <PieChart>
                    <Pie
                      data={speakerDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={140}
                      paddingAngle={5}
                      dataKey="percentage"
                      label={({ speaker, percentage }) => `${speaker}: ${percentage}%`}
                      labelLine={{ stroke: '#999', strokeWidth: 1 }}
                    >
                      {speakerDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Key Moments */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  🎯 Key Moments Detected by AI
                </Typography>
                <List>
                  {keyMoments.map((moment, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        bgcolor: alpha(
                          moment.engagement >= 85 ? colors.success : moment.engagement >= 70 ? colors.warning : colors.danger,
                          0.08
                        ),
                        borderRadius: 2,
                        mb: 1.5,
                        border: `2px solid ${alpha(
                          moment.engagement >= 85 ? colors.success : moment.engagement >= 70 ? colors.warning : colors.danger,
                          0.3
                        )}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                        <Chip
                          icon={<TimeIcon sx={{ fontSize: 14 }} />}
                          label={moment.time}
                          size="small"
                          sx={{
                            bgcolor: alpha(colors.primary, 0.15),
                            color: colors.primary,
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label={`${moment.engagement}% engagement`}
                          size="small"
                          sx={{
                            bgcolor: alpha(
                              moment.engagement >= 85 ? colors.success : moment.engagement >= 70 ? colors.warning : colors.danger,
                              0.2
                            ),
                            color: moment.engagement >= 85 ? colors.success : moment.engagement >= 70 ? colors.warning : colors.danger,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {moment.moment}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {moment.description}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {selectedView === 'topics' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                📚 Topics Extracted & Sentiment Analysis
              </Typography>
              {topicsExtracted.map((topic, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {topic.topic}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={`${topic.mentions} mentions`}
                        size="small"
                        sx={{ bgcolor: alpha(colors.primary, 0.1), color: colors.primary }}
                      />
                      <Chip
                        label={`${(topic.sentiment * 100).toFixed(0)}% positive`}
                        size="small"
                        sx={{
                          bgcolor: alpha(
                            topic.sentiment >= 0.8 ? colors.success : topic.sentiment >= 0.6 ? colors.warning : colors.danger,
                            0.15
                          ),
                          color: topic.sentiment >= 0.8 ? colors.success : topic.sentiment >= 0.6 ? colors.warning : colors.danger,
                        }}
                      />
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={topic.sentiment * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(colors.primary, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: topic.sentiment >= 0.8 ? colors.success : topic.sentiment >= 0.6 ? colors.warning : colors.danger,
                        borderRadius: 3,
                      }
                    }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      )}

      {selectedView === 'sentiment' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Sentiment Analysis Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={450}>
                <AreaChart data={sentimentOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segment" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke={colors.success} fill={alpha(colors.success, 0.7)} name="Positive" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke={colors.warning} fill={alpha(colors.warning, 0.6)} name="Neutral" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke={colors.danger} fill={alpha(colors.danger, 0.5)} name="Negative" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {selectedView === 'questions' && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Question Categories
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={questionCategories} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="category" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={colors.primary} radius={[0, 8, 8, 0]}>
                    {questionCategories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors.chart[index % colors.chart.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Speaker Time Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={speakerDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={130}
                    paddingAngle={5}
                    dataKey="percentage"
                    label={({ speaker, percentage }) => `${speaker}: ${percentage}%`}
                    labelLine={{ stroke: '#999', strokeWidth: 1 }}
                  >
                    {speakerDistribution.map((entry, index) => (
                      <Cell key={`speaker-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default TranscriptAnalysis;